// Jotly Desktop — chat side-panel (Cmd+/). Slides in from the right. Wired to
// the real agentic recall backend (POST /chat/ask) — ask a vague question, get
// the synthesised answer plus the citations it leaned on. Captures are embedded
// now, so "hand the thing back" returns dumps too (the wedge hero flow).

import { useEffect, useRef, useState } from 'react';
import { IconJD } from './atoms';
import { askQuestion, type AskResponse } from '../../api/ask';

type ChatTurn = {
  id: string;
  role: 'you' | 'jotly';
  text: string;
  pending?: boolean;
  citations?: AskResponse['citations'];
};

let seq = 0;
const nextId = () => `c${Date.now()}_${seq++}`;

export function ChatPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns]);

  const send = async () => {
    const q = text.trim();
    if (!q || busy) return;
    setText('');
    setTurns((prev) => [...prev, { id: nextId(), role: 'you', text: q }]);
    const replyId = nextId();
    setTurns((prev) => [...prev, { id: replyId, role: 'jotly', text: '', pending: true }]);
    setBusy(true);
    try {
      const res = await askQuestion(q);
      setTurns((prev) =>
        prev.map((t) =>
          t.id === replyId ? { ...t, text: res.answer, citations: res.citations, pending: false } : t,
        ),
      );
    } catch (e) {
      setTurns((prev) =>
        prev.map((t) =>
          t.id === replyId
            ? {
                ...t,
                text: e instanceof Error ? `Couldn't answer just now — ${e.message}` : "Couldn't answer just now.",
                pending: false,
              }
            : t,
        ),
      );
    } finally {
      setBusy(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  if (!open) return null;

  return (
    <aside className="jd-chat">
      <div className="jd-chat-h">
        <IconJD name="chat" size={18} color="var(--accent)" stroke={1.8} />
        <span className="title">Chat with Jotly</span>
        <button type="button" className="jd-icon-btn" onClick={onClose} title="Close (⌘/)">
          <IconJD name="x" size={16} color="currentColor" />
        </button>
      </div>

      <div className="jd-chat-body" ref={bodyRef}>
        {turns.length === 0 ? (
          <div className="jd-chat-empty">
            Ask for anything you half-remember. &ldquo;that thing from the bbq weekend&rdquo; lands even if you never
            said &ldquo;bbq&rdquo;.
          </div>
        ) : (
          turns.map((t) =>
            t.role === 'you' ? (
              <div key={t.id} className="jd-chat-bubble user">
                {t.text}
              </div>
            ) : (
              <div key={t.id} className="jd-chat-bubble ai">
                <div className="row">
                  <span className="dot">·</span>
                  <span className={t.pending ? 'pending-text' : ''}>{t.text || (t.pending ? 'Thinking…' : '')}</span>
                </div>
                {t.citations && t.citations.length > 0 && (
                  <div className="jd-chat-cites">
                    {t.citations.map((c) => (
                      <div key={`${c.sourceType}-${c.id}`} className="jd-chat-cite">
                        <span className="jt-tag">{c.sourceType}</span>
                        <span className="cite-title">{c.title}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ),
          )
        )}
      </div>

      <div className="jd-chat-input">
        <input
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void send();
            }
          }}
          placeholder="Ask Jotly…"
        />
        <button
          type="button"
          className="jd-icon-btn"
          style={{ background: 'var(--accent)', color: '#fff' }}
          onClick={() => void send()}
          disabled={busy || text.trim().length === 0}
        >
          <IconJD name="arrow-up" size={14} color="currentColor" stroke={2.4} />
        </button>
      </div>
    </aside>
  );
}
